import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaStar } from "react-icons/fa";
import { motion } from "framer-motion";

import bannerOne from "../../../assets/HeroSection/hero-banner-1.png";
import bannerTwo from "../../../assets/HeroSection/hero-banner-2.png";
import { DS } from "../../../utils/designSystem";
import { fadeIn } from "../../common/motionFrameVarients";

const STATS = [
  { value: "10K+", label: "Students" },
  { value: "200+", label: "Courses" },
  { value: "50+",  label: "Instructors" },
];

const HeroSection = () => {
  return (
    <section className="relative overflow-hidden bg-richblack-900 pt-12 pb-14 md:pt-16 md:pb-20">
      {/* Background ambient blobs */}
      <div
        aria-hidden
        className={`pointer-events-none absolute -top-32 right-0 h-[560px] w-[560px] rounded-full blur-3xl ${DS.gradient.heroBlobTop}`}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute -bottom-24 -left-16 h-[400px] w-[400px] rounded-full blur-3xl ${DS.gradient.heroBlobBtm}`}
      />

      <div className={DS.layout.container}>
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-16">

          {/* ── Left: copy ─────────────────────────────────────────── */}
          <motion.div
            variants={fadeIn("right", 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="flex flex-1 flex-col items-center text-center lg:items-start lg:text-left"
          >
            {/* Badge */}
            <span className={`${DS.badge.neutral} mb-6`}>
              <span className="h-2 w-2 animate-pulse rounded-full bg-caribbeangreen-100" />
              Learn from industry experts
            </span>

            {/* Headline */}
            <h1 className={`${DS.text.display} mb-6 max-w-xl`}>
              Elevate Your{" "}
              <span className={DS.gradient.highlight}>Expertise</span>{" "}
              with Coding Skills
            </h1>

            {/* Sub-copy */}
            <p className={`${DS.text.body} mb-8 max-w-lg`}>
              Elevate your skills with our ultimate program for unparalleled
              enhancement and mastery. Your success journey starts with just one
              click.
            </p>

            {/* CTAs */}
            <div className="flex flex-col gap-4 sm:flex-row">
              <Link to="/signup" className={DS.btn.primary}>
                Find Courses <FaArrowRight className="text-sm" />
              </Link>
              <Link to="/about" className={DS.btn.secondary}>
                Learn More
              </Link>
            </div>

            {/* Stats row */}
            <div className="mt-10 flex items-center gap-8 border-t border-richblack-700 pt-8">
              {STATS.map(({ value, label }) => (
                <div key={label} className="flex flex-col items-center lg:items-start">
                  <span className="text-2xl font-bold text-richblack-5">{value}</span>
                  <span className={DS.text.caption}>{label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ── Right: images ──────────────────────────────────────── */}
          <motion.div
            variants={fadeIn("left", 0.15)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="relative w-full flex-shrink-0 max-w-md lg:w-[46%] lg:max-w-none"
          >
            <div className="grid grid-cols-2 gap-4">
              {/* Image 1 */}
              <div className="overflow-hidden rounded-[48px_12px_12px_48px] shadow-[0_20px_60px_rgba(0,0,8,0.6)]">
                <img
                  src={bannerOne}
                  alt="Student learning"
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>
              {/* Image 2 - offset down */}
              <div className="mt-14 overflow-hidden rounded-[12px_48px_48px_12px] shadow-[0_20px_60px_rgba(0,0,8,0.6)]">
                <img
                  src={bannerTwo}
                  alt="Student at laptop"
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            </div>

            {/* Floating rating card */}
            <div
              className={`absolute -bottom-4 left-4 flex items-center gap-3 ${DS.card.base} ${DS.card.pad} ${DS.shadow.card}`}
            >
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-yellow-50/10 text-sm font-bold text-yellow-50">
                4.8
              </div>
              <div>
                <div className="flex items-center gap-0.5 text-yellow-50 text-xs">
                  {[...Array(5)].map((_, i) => (
                    <FaStar key={i} className={i < 5 ? "opacity-100" : "opacity-30"} />
                  ))}
                </div>
                <p className={`${DS.text.caption} mt-0.5`}>12,000+ reviews</p>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
